import { useRef, useState, useEffect } from "react"

import { Box, Divider, Stack, Tab, Tabs, Link } from "@mui/material"
import { styled } from "@mui/material/styles"
import { nanoid } from "nanoid"

import type { FC } from ".."
import {
  Container,
  FullBody,
  SectionAccordion,
  SplitBody,
  SplitImageText,
  SplitList,
  Weather,
} from ".."

export type SectionContentTypes =
  | "text"
  | "two_column_text"
  | "includes_excludes_list"
  | "split_image_and_text"
  | "weather_guide"
  | "accordion"

interface SectionProps {
  title: string
  components: {
    type: SectionContentTypes
    content: any
  }[]
}

interface SectionSwitcherProps {
  id?: string
  items: SectionProps[]
}

const SectionSwitcher: FC<SectionSwitcherProps> = ({ id, items }) => {
  const uniqueId = useRef(nanoid(12))
  const [activeIndex, setActiveIndex] = useState(0)

  //Slugify title and check if hash exists then scroll
  const slugify = (title: string | null | undefined) => {
    if (!title) return "" // Return an empty string if title is null or undefined
    return title.toLowerCase().replace(/\s+/g, "-")
  }
  const sectionRefs = useRef<(HTMLDivElement | null)[]>([])
  useEffect(() => {
    const hash = window.location.hash.replace("#", "")
    const foundIndex = items.findIndex((item) => slugify(item.title) === hash)
    if (foundIndex >= 0) {
      setActiveIndex(foundIndex)
      requestAnimationFrame(() => {
        setTimeout(() => scrollToSection(foundIndex), 1000)
      })
    }
  }, [items])
  const scrollToSection = (index: number) => {
    sectionRefs.current[index]?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    })
  }

  return (
    <Container id={id} mb={{ md: 40 }}>
      <StyledTabs
        value={activeIndex}
        onChange={(_, tabIndex) => setActiveIndex(tabIndex)}
        textColor="primary"
        indicatorColor="primary"
        aria-label="section switcher"
        variant="scrollable"
        scrollButtons={false}
      >
        {items?.map(({ title }, i) => (
          <Link
            key={title || `section-link-${i}`}
            href={`#${slugify(title)}`}
            onClick={(e) => {
              e.preventDefault()
              setActiveIndex(i)
              window.history.pushState(null, "", `#${slugify(title)}`)
            }}
          >
            <StyledTab
              key={title || `section-tab-${i}`}
              value={i}
              label={title}
              id={`section-switcher-tab-${uniqueId.current}-${i}`}
              ref={(ref) => (sectionRefs.current[i] = ref)}
              aria-controls={`section-switcher-tab-${uniqueId.current}-${i}`}
              disableRipple
            />
          </Link>
        ))}
      </StyledTabs>
      <StyledDivider variant="middle" light />

      {items?.map(({ components, title }, i) => (
        <Box
          key={title || `section-panel-${i}`}
          role="tabpanel"
          id={`section-switcher-panel-${uniqueId.current}-${i}`}
          aria-labelledby={`section-switcher-tab-${uniqueId.current}-${i}`}
          style={{
            display: activeIndex === i ? "block" : "none",
          }}
        >
          <SectionSwitcherContent {...{ components }} />
        </Box>
      ))}

      <StyledContentWrapper sx={{ mt: 30, mx: 30 }}></StyledContentWrapper>
    </Container>
  )
}

export default SectionSwitcher
type ContentProps = Pick<SectionProps, "components">
const SectionSwitcherContent: FC<ContentProps> = ({ components }) => {
  return (
    <Stack rowGap={{ xs: 20, md: 30 }}>
      {components?.map(({ type, content }, i) => {
        switch (type) {
          case "accordion":
            return <SectionAccordion key={`accordion-${i}`} {...content} />
          case "text":
            return <FullBody key={`text-${i}`} {...content} />
          case "two_column_text":
            return <SplitBody key={`two-column-text-${i}`} {...content} />
          case "split_image_and_text":
            return <SplitImageText key={`split-image-and-text-${i}`} {...content} />
          case "includes_excludes_list":
            return <SplitList key={`includes-excludes-list-${i}`} {...content} />
          case "weather_guide":
            return <Weather key={`weather-guide-${i}`} {...content} />
          default:
            return null
        }
      })}
    </Stack>
  )
}

const StyledTab = styled(Tab)(({ theme }) => ({
  ...theme.typography["link.sm"],
  minHeight: theme.spacing(23.5),
  color: theme.palette["neutral.700"].main,
  opacity: 1,

  [theme.breakpoints.down("md")]: {
    ...theme.typography["link.sm"],
    minHeight: theme.spacing(17),
  },
}))

const StyledTabs = styled(Tabs)(
  ({ theme }) => `
    .MuiTabs-flexContainer {
      justify-content: center;
      gap: ${theme.spacing(20)};
      
      ${theme.breakpoints.down("lg")} {
        justify-content: start;
        gap: ${theme.spacing(10)};
        margin: ${theme.spacing(0, 5)};
      }
    }

    .MuiTabs-indicator {
      background-color: ${theme.palette["primary.800"].main};
      height: 2px;
    }
  `
)

const StyledContentWrapper = styled(Box)(
  ({ theme }) => `
    display: grid;
    grid-gap:  ${theme.spacing(10, 20)};
    margin: ${theme.spacing(30, 100)};
    ${theme.breakpoints.down("xl")} {
      margin: ${theme.spacing(15, 5)};
    }
    ${theme.breakpoints.down("md")} {
      margin: ${theme.spacing(15, 0, 0)};
    }
  `
)

const StyledDivider = styled(Divider)(
  ({ theme }) => `
    background: ${theme.palette["neutral.200"].main};
    margin: -1px ${theme.spacing(0, 30)};
    

    ${theme.breakpoints.down("md")} {
      margin: -1px ${theme.spacing(0, 15)};
    }
  `
)
